// This is the Register page
// New users land here after clicking their invite link from email
// The invite token is read from the URL and validated before showing the form

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import config from "../config";

const API_URL = config.API_URL;

export default function Register() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Validate the invite token and pre-fill org/email info
    if (!token) {
      setError("Invalid invite link. Please request access again.");
      setLoading(false);
      return;
    }

    axios
      .get(`${API_URL}/auth/validate-invite/${token}`)
      .then((res) => {
        setInviteInfo(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError("This invite link is invalid or has expired. Please request access again.");
        setLoading(false);
      });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(
        `${API_URL}/auth/register-invite?token=${encodeURIComponent(token)}&full_name=${encodeURIComponent(fullName)}&password=${encodeURIComponent(password)}`
      );
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-white text-xl">Validating invite...</p>
    </div>
  );

  if (success) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <div className="w-full max-w-md text-center">
        <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <span className="text-white font-bold text-2xl">✓</span>
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">Account Created!</h1>
        <p className="text-gray-400 text-sm mb-6">
          Your account has been created for <strong className="text-white">{inviteInfo?.organization_name}</strong>. You can now sign in.
        </p>
        <button
          onClick={() => navigate("/login")}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
        >
          Go to Sign In
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="w-full max-w-md">

        {/* Logo and title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">DW</span>
          </div>
          <h1 className="text-3xl font-bold text-white">Create Account</h1>
          {inviteInfo && (
            <p className="text-gray-400 mt-2">
              You've been invited to join <strong className="text-white">{inviteInfo.organization_name}</strong>
            </p>
          )}
        </div>

        <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">

          {error && (
            <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
              {error.includes("invalid or has expired") && (
                <div className="mt-2">
                  <button
                    onClick={() => navigate("/request-access")}
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Request access again
                  </button>
                </div>
              )}
            </div>
          )}

          {inviteInfo && (
            <div className="mb-5">
              <label className="block text-gray-400 text-sm mb-2">Email Address</label>
              <input
                type="email"
                value={inviteInfo.email}
                disabled
                className="w-full bg-gray-800 border border-gray-700 text-gray-400 rounded-lg px-4 py-3 text-sm cursor-not-allowed"
              />
            </div>
          )}

          <form onSubmit={handleSubmit}>

            <div className="mb-5">
              <label className="block text-gray-400 text-sm mb-2">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Bruce Wayne"
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                required
              />
            </div>

            <div className="mb-5">
              <label className="block text-gray-400 text-sm mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-400 text-sm mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Creating Account..." : "Create Account"}
            </button>

          </form>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/login")}
            className="text-gray-500 hover:text-gray-400 text-sm transition-colors"
          >
            Back to Sign In
          </button>
        </div>

      </div>
    </div>
  );
}